import React from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Alert, Button, Checkbox } from '@freelancelabs/components';

import { ListSkeleton } from '../../../components/Skeletons';
import { AssignableDashboardsDocument } from '../../../graphql/__generated__/types';
import { dashboardEmptiness } from './catalogue';
import { Draft, DraftDashboard } from './draft';

interface Props {
    draft: Draft;
    onChange: (patch: Partial<Draft>) => void;
}

/**
 * Step 3: the dashboards, an optional attribution. The catalogue is a whitelist (declaring an entry
 * IS the authorisation), grouped by account, not the free-text Metabase URL the mockup has.
 */
const DashboardsStep = ({ draft, onChange }: Props) => {
    const organizationIds = draft.attachments.map((attachment) => attachment.organizationId);

    // Narrowed to the accounts step 2 kept: a dashboard outside the selection is refused at the send.
    const { data, loading, error, refetch } = useQuery(AssignableDashboardsDocument, {
        variables: { organizationIds },
    });

    const dashboards = (data?.assignableDashboards?.dashboards ?? []).filter(Boolean);
    const emptiness = dashboardEmptiness(error, data?.assignableDashboards?.emptyReason);

    const selectedIds = new Set(draft.dashboards.map((dashboard) => dashboard.id));

    const toggle = (dashboard: DraftDashboard, checked: boolean) =>
        onChange({
            dashboards: checked
                ? [...draft.dashboards, dashboard]
                : draft.dashboards.filter((entry) => entry.id !== dashboard.id),
        });

    // Grouped by account in the order the API sent them, on the id and never the name: distinct
    // accounts genuinely share a name in this base, so grouping by name would merge two companies.
    const groups = dashboards.reduce<
        { organizationId: string; organizationName: string; entries: typeof dashboards }[]
    >((acc, dashboard) => {
        const organizationId = dashboard.organizationId as string;
        const last = acc[acc.length - 1];
        if (last && last.organizationId === organizationId) {
            last.entries.push(dashboard);
            return acc;
        }
        return [
            ...acc,
            {
                organizationId,
                organizationName: dashboard.organizationName ?? c('Onboarding dashboards').t`Account without a name`,
                entries: [dashboard],
            },
        ];
    }, []);

    const count = draft.dashboards.length;

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h2 className="text-sm font-semibold text-text-900">{c('Onboarding dashboards')
                    .t`Dashboards to grant`}</h2>
            </div>

            {loading && <ListSkeleton rows={4} withControl />}

            {!loading && emptiness && (
                <Alert type={emptiness.tone} title={emptiness.title}>
                    {emptiness.guidance && <p className="text-sm">{emptiness.guidance}</p>}
                    {emptiness.detail && (
                        <p className="mt-2 break-words font-mono text-xs text-text-600">{emptiness.detail}</p>
                    )}
                    {/* Only where retrying is the fix; wrapped so the button doesn't stretch to the alert width. */}
                    {error && (
                        <div className="mt-2">
                            <Button category="weak" size="small" onClick={() => refetch()}>
                                {c('Action').t`Retry`}
                            </Button>
                        </div>
                    )}
                </Alert>
            )}

            {groups.map((group) => (
                <section key={group.organizationId} className="rounded-lg border border-border-norm p-4">
                    <h3 className="text-sm font-semibold text-text-900">{group.organizationName}</h3>
                    <ul className="mt-3 flex flex-col gap-2">
                        {group.entries.map((dashboard) => {
                            const id = dashboard.id as string;
                            // perimeterLocked false: no locked parameter carries the perimeter, so the
                            // token maker refuses to sign. Greyed with its reason rather than hidden.
                            const locked = dashboard.perimeterLocked === true;
                            const reasonId = locked ? undefined : `dashboard-reason-${id}`;
                            return (
                                <li key={id}>
                                    <Checkbox
                                        id={`dashboard-${id}`}
                                        checked={selectedIds.has(id)}
                                        disabled={!locked}
                                        aria-describedby={reasonId}
                                        onChange={(event) =>
                                            toggle(
                                                { id, organizationId: dashboard.organizationId as string },
                                                event.target.checked
                                            )
                                        }
                                    >
                                        <span className="text-sm">
                                            <b>{dashboard.name}</b>
                                            {!locked && (
                                                <span id={reasonId} className="ml-2 text-xs text-warning-600">
                                                    {c('Onboarding dashboards')
                                                        .t`Cannot be granted: this entry declares no locked perimeter parameter, so the access would be signed for nobody and open nothing. It has to be corrected where the catalogue is declared.`}
                                                </span>
                                            )}
                                        </span>
                                    </Checkbox>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ))}

            {!loading && !emptiness && (
                <p className="text-xs text-text-600">
                    {ngettext(msgid`${count} dashboard selected.`, `${count} dashboards selected.`, count)}
                </p>
            )}
        </div>
    );
};

export default DashboardsStep;
